import { h } from 'preact';
import Store from 'store/store';
import { post } from 'utils/api';
import { getLiveUrlFromPageData, hasUnsavedChanges } from 'utils/data';
import formatDateTime from 'utils/formatDateTime';
import * as jsonPointer from 'utils/jsonPointer';
import { Modal, showModal, alert } from 'utils/modal';
import { getPreviewDataFromData } from 'utils/preview';
import { isPowsterEmployee } from 'utils/roles';
import { joinClasses, triggerAnim } from 'utils/utils';

import Button from 'components/button/button';
import SimpleField from 'components/editor/settings/simpleField';
import DateField from 'components/editor/settings/dateField';
import LinkIcon from 'components/icons/link';
import Toggle from 'components/toggle/toggle';
import Warning from 'components/warning/warning';

import s from 'utils/createShareLinkModal.sss';

export class CreateShareLinkModal extends Modal {
	s = s;

	title = 'Share preview';

	constructor(props) {
		super(props);
		this.state = { loading: false };
		this.linksCache = {};

		this.createLink = this.createLink.bind(this);
	}

	componentDidMount() {
		super.componentDidMount?.();
		const dateField = this.base.querySelector('input[name="expiration"]');
		if (dateField) {
			const expiry = new Date();
			expiry.setDate(expiry.getDate() + 7);
			dateField.value = formatDateTime(expiry, 'yyyy-MM-dd');
		}
		// TODO: close on navigation
	}

	async generateLink(formData) {
		const data = jsonPointer.get(Store.get().data, this.props.path)?.toJS();
		if (!data) throw new Error('No data found for the given path');
		const type = data.section_type ? 'section_id' : 'movie_id';
		const result = await post('/preview/', {
			live_url: getLiveUrlFromPageData(data),
			content: getPreviewDataFromData(data),
			password: formData.security,
			expiry: formData.expiration,
			demo_mode: !!formData.demoMode,
			[type]: data.id,
		});
		if (!result.id) throw new Error('No ID returned from the server');
		const link = new URL('./preview/', location.href);
		link.searchParams.set('x', result.id);
		return link.href;
	}

	async createLink() {
		this.setState({ loading: true });
		try {
			const form = this.base.querySelector('form');
			if (!form.reportValidity()) {
				return this.setState({ loading: false });
			}
			const formData = Object.fromEntries(new FormData(form));
			const id = [this.props.path, ...Object.values(formData)].join('~~');

			const link = this.linksCache[id] || await this.generateLink(formData);
			this.linksCache[id] = link;

			await navigator.clipboard.writeText(link);
			triggerAnim(this.base.querySelector(`.${s.create}`), s.success);
			this.setState({ success: true });
		} catch {
			alert('Sorry, an error occurred while generating this preview link', { title: 'Error' });
		}
		this.setState({ loading: false });
	}

	renderContent() {
		const { path } = this.props;

		return (
			<div class={this.c('content')}>
				<DateField label="Link expiry date" name="expiration" required min={formatDateTime(new Date(), 'yyyy-MM-dd')} />
				<SimpleField type="password" label="Password (optional)" name="security" />
				{isPowsterEmployee() && <Toggle name="demoMode">Use demo build of thundr app</Toggle>}
				{!!hasUnsavedChanges(path) && (
					<Warning class={s.warning}>
						<div>You have unsaved changes</div>
						<div>These changes will be included in the preview link</div>
					</Warning>
				)}
			</div>
		);
	}

	renderActions() {
		return (
			<div class={this.c('actions')}>
				<Button
					type="button"
					class={joinClasses(s.create, this.state.loading && s.loading, this.state.success && s.success)}
					primary
					icon={LinkIcon}
					iconAlt="check"
					iconSize="small"
					onClick={this.createLink}
					data-success-text="Copied"
				>
					Copy link
				</Button>
			</div>
		);
	}
}

export async function showCreateShareLinkModal({ path }) {
	return await showModal(CreateShareLinkModal, { path });
}